import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './config';
import { Segmented, MODEL_ACCESS } from './AccessControl';
import Nav from './Nav';
import { Explain } from './Explain';

const ALL_GROUP_OBJECT_ID = '00000000-0000-0000-0000-000000000002';

// Model visibility (the second privacy axis): sealed by default, but the
// choice PERSISTS as this user's own default (owner decision 2026-07-05 —
// comments default open; the create page keeps a user-settable default).
// The remembered default moved from a boolean to the three-valued model axis (2026-08-08).
// A NEW KEY on purpose: the old one stored 'true'/'false', and reading that as a model_access
// value would silently seal every object for a user whose stored default was 'true'.
const MODEL_ACCESS_KEY = 'wv-create-model-access';

function CreateObject() {
  const [objectType, setObjectType] = useState('text');
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modelAccess, setModelAccess] = useState(() => {
    const stored = localStorage.getItem(MODEL_ACCESS_KEY);
    return MODEL_ACCESS.some((m) => m.id === stored) ? stored : 'sealed';
  });

  const chooseModelAccess = (id) => {
    localStorage.setItem(MODEL_ACCESS_KEY, id);
    setModelAccess(id);
  };

  // Visibility: array of { id, content } objects (default: 'all' group)
  const [visibilityTargets, setVisibilityTargets] = useState([]);

  // Search state
  const [visSearch, setVisSearch] = useState('');
  const [visResults, setVisResults] = useState([]);
  const [visSearchOpen, setVisSearchOpen] = useState(false);
  const searchDebounce = useRef(null);
  const searchRef = useRef(null);

  const navigate = useNavigate();

  // Load the 'all' group on mount and set as default target
  useEffect(() => {
    apiFetch(`/api/objects/${ALL_GROUP_OBJECT_ID}`)
      .then(r => r.ok ? r.json() : null)
      .then(obj => {
        if (obj) {
          setVisibilityTargets([obj]);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search groups as user types
  useEffect(() => {
    if (!visSearch.trim()) {
      setVisResults([]);
      return;
    }
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      try {
        const r = await apiFetch(`/api/objects?object_type=group&q=${encodeURIComponent(visSearch)}`);
        if (r.ok) {
          const results = await r.json();
          // Filter out already-selected targets
          const selectedIds = new Set(visibilityTargets.map(t => t.id));
          setVisResults(results.filter(g => !selectedIds.has(g.id)));
        }
      } catch {}
    }, 250);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visSearch, visibilityTargets]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setVisSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addTarget = (obj) => {
    setVisibilityTargets(prev => [...prev, obj]);
    setVisSearch('');
    setVisResults([]);
    setVisSearchOpen(false);
  };

  const removeTarget = (id) => {
    setVisibilityTargets(prev => prev.filter(t => t.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    try {
      setSubmitting(true);
      const response = await apiFetch('/api/objects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: content.trim(),
          object_type: objectType,
          visibility_targets: visibilityTargets.map(t => t.id),
          model_access: modelAccess,
        }),
      });

      if (!response.ok) throw new Error('Failed to create object');

      const newObject = await response.json();
      navigate(`/object/${newObject.id}`);
    } catch (err) {
      setError('Failed to create object');
      console.error('Error creating object:', err);
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Nav />
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 20px 20px' }}>
        <h1 style={{ marginTop: 0 }}>Create Object</h1>

        {error && <div style={{ color: 'var(--wv-neg)', marginBottom: '10px' }}>{error}</div>}

        {/* Type selector — the house segmented pill (AccessControl.Segmented),
            same control the model-access row below already uses; the previous
            hand-rolled square tabs were the page's one off-token control */}
        <div style={{ marginBottom: 'var(--wv-sp-8)' }}>
          {/* question added 2026-07-28: something to be settled, not asserted.
              Carries no truth value and no trust score — see the note under the
              field below and trust/graph.ts's UNSCORED_OBJECT_TYPES. */}
          <Segmented
            options={['text', 'link', 'tag', 'question'].map((t) => ({ id: t, label: t }))}
            value={objectType}
            onChange={(t) => { setObjectType(t); setContent(''); }}
            ariaLabel="object type"
          />
        </div>

        <form onSubmit={handleSubmit}>
          {objectType === 'text' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter text..."
                rows={6}
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                autoFocus
              />
            </div>
          )}

          {objectType === 'link' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>URL</label>
              <input
                type="url"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="https://..."
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                autoFocus
              />
            </div>
          )}

          {objectType === 'question' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>Question</label>
              <input
                type="text"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="e.g. what constitutes racism"
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                autoFocus
              />
              {/* Text-minimal (standing UI principle 2026-07-28): one line here,
                  the ⓘ carries the rest. Never a trust band — a question has no
                  truth value, so a score for it would be meaningless, and a
                  meaningless score renders exactly like a meaningful one. */}
              <div className="trust-muted" style={{ fontSize: '12px', marginTop: '6px' }}>
                <Explain concept="question">Not scored — questions are settled, not asserted.</Explain>
              </div>
            </div>
          )}

          {objectType === 'tag' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>Tag name</label>
              <input
                type="text"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="e.g. machine_learning"
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                autoFocus
              />
            </div>
          )}

          {/* Visibility */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>Visible to</label>

            {/* Selected targets as chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {visibilityTargets.length === 0 && (
                <span style={{ fontSize: '13px', color: 'var(--wv-muted)' }}>Only you (private)</span>
              )}
              {/* chip-with-remove, on TagField's pattern — the explicit
                  minWidth/minHeight: 0 keeps the coarse-pointer button
                  minimum from inflating the chip on touch */}
              {visibilityTargets.map(t => (
                <span key={t.id} className="wv-chip wv-tint" style={{ gap: 'var(--wv-sp-1)', cursor: 'default' }}>
                  {t.content}
                  <button
                    type="button"
                    onClick={() => removeTarget(t.id)}
                    aria-label={`Remove ${t.content}`}
                    title={`Remove ${t.content}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      minWidth: 0,
                      minHeight: 0,
                      lineHeight: 1,
                      color: 'var(--wv-muted)',
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {/* Search to add groups */}
            <div ref={searchRef} style={{ position: 'relative', maxWidth: '300px' }}>
              <input
                type="text"
                value={visSearch}
                onChange={(e) => { setVisSearch(e.target.value); setVisSearchOpen(true); }}
                onFocus={() => setVisSearchOpen(true)}
                placeholder="Add group..."
                style={{ width: '100%', padding: '6px 8px', boxSizing: 'border-box', fontSize: '13px' }}
              />
              {visSearchOpen && visResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  border: '1px solid var(--wv-border)',
                  backgroundColor: 'var(--wv-surface)',
                  zIndex: 10,
                  maxHeight: '160px',
                  overflowY: 'auto',
                }}>
                  {visResults.map(g => (
                    <div
                      key={g.id}
                      onMouseDown={() => addTarget(g)}
                      style={{
                        padding: '6px 10px',
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--wv-surface2)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--wv-surface)'}
                    >
                      {g.content}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* The MODEL axis — three values since 2026-08-08, not a checkbox. The HUMAN axis is
              the target picker above, which is why this page takes only half of AccessControl:
              its picker accepts arbitrary targets and is strictly more expressive than that
              component's three-way row. The choice persists as this user's default. */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>Which models may read it</label>
            <Segmented
              options={MODEL_ACCESS}
              value={modelAccess}
              onChange={chooseModelAccess}
              ariaLabel="which models may read it"
            />
            <div style={{ fontSize: '13px', color: 'var(--wv-muted)', marginTop: '6px', maxWidth: '560px' }}>
              {MODEL_ACCESS.find((m) => m.id === modelAccess)?.blurb} Your choice is remembered as your default.
            </div>
          </div>

          <button type="submit" disabled={!content.trim() || submitting}>
            {submitting ? 'Creating...' : 'Create'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreateObject;
